import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import {
  withContacts,
  withStatus,
  type HouseholdWithStatus,
  type PersonContact,
  type PersonWithContact,
  type Turf,
  type Visit,
} from '@yucanvas/shared';
import { useAuth } from '../auth/AuthProvider';
import { db, type LocalHouseNote } from '../offline/db';
import { syncNow } from '../offline/sync';
import { fetchTurfReview, type ReviewVisit } from './turf-review';

interface TurfData {
  turf: Turf | null;
  rows: HouseholdWithStatus[];
  loading: boolean;
  error: string | null;
  reload: () => void;
  /**
   * True when this turf is being read over the network because it is not
   * assigned to the viewer -- an organizer looking in. Nothing on the screen
   * may write: RLS grants organizers the reads and not the matching inserts
   * (20260915000100_rls_evaluate_once.sql), and there is no device store here
   * for the offline queue to write into either.
   */
  readOnly: boolean;
  /** Registered voters at one address, with their latest contact attached. */
  peopleAt: (householdId: string) => PersonWithContact[];
  /** Notes about one address, oldest first. Append-only. */
  notesAt: (householdId: string) => LocalHouseNote[];
  /** Every visit at one address, newest first. */
  visitsAt: (householdId: string) => ReviewVisit[];
}

const TurfDataContext = createContext<TurfData | null>(null);

/**
 * Reads the device store, or -- for an organizer opening turf that is not
 * theirs -- the network.
 *
 * The device path is the one that matters: a visit recorded seconds ago and a
 * household pulled last week are the same kind of row there, which is what
 * makes the screens behave identically on and off signal (PLAN 5.2). Dexie's
 * live query re-runs whenever either table changes, so the sync loop writing
 * in the background updates the map with no wiring between them.
 *
 * The network path exists so looking at a turf does not require taking it
 * (data/turf-review.ts). It is a one-shot fetch with an explicit reload, and
 * it never writes -- which is why it also sets `readOnly`.
 */
export function TurfDataProvider({
  turfId,
  children,
}: {
  turfId: string;
  children: ReactNode;
}) {
  const { profile } = useAuth();

  // `?? null` is load-bearing: Dexie returns undefined both while the query is
  // in flight and for a row that is not there, and those decide different
  // things here. undefined means "still asking", null means "not assigned to
  // this device" -- which is the signal to go to the network.
  const localTurf = useLiveQuery(async () => (await db.turfs.get(turfId)) ?? null, [
    turfId,
  ]);

  const mayReview = profile?.role === 'organizer' || profile?.role === 'admin';
  const reviewing = localTurf === null && mayReview;

  const local = useLiveQuery(async () => {
    const all = await db.households.where('turf_id').equals(turfId).toArray();
    // Soft-deleted rows are filtered here rather than on the server: a delete
    // has to reach the device as an update, or it could never propagate at all
    // (PLAN 5.6).
    const households = all.filter((h) => h.active);
    const ids = households.map((h) => h.id);
    const visits = (await db.visits.where('household_id').anyOf(ids).toArray()) as Visit[];

    const people = await db.people.where('household_id').anyOf(ids).toArray();
    const contacts = (await db.personContacts
      .where('person_id')
      .anyOf(people.map((p) => p.id))
      .toArray()) as unknown as PersonContact[];

    const notes = await db.houseNotes.where('household_id').anyOf(ids).toArray();

    // The device has no roster, so a visit here carries no canvasser name.
    const named = visits.map((v) => ({ ...v, canvasser_name: null }));
    return { households, visits: named, people, contacts, notes };
  }, [turfId]);

  const [remote, setRemote] = useState<Awaited<
    ReturnType<typeof fetchTurfReview>
  > | null>(null);
  const [remoteError, setRemoteError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!reviewing) {
      setRemote(null);
      setRemoteError(null);
      return;
    }
    // Guarded rather than aborted: the fetch is five requests deep and the
    // only thing a late result could do is repaint a screen that has moved on.
    let live = true;
    setRemote(null);
    setRemoteError(null);
    fetchTurfReview(turfId)
      .then((data) => {
        if (live) setRemote(data);
      })
      .catch((cause: unknown) => {
        if (live) {
          setRemoteError(
            cause instanceof Error ? cause.message : 'Could not load this turf.',
          );
        }
      });
    return () => {
      live = false;
    };
  }, [reviewing, turfId, attempt]);

  const source = reviewing ? remote ?? undefined : local;

  const rows = useMemo(
    () => (source ? withStatus(source.households, source.visits) : []),
    [source],
  );

  // Indexed once per pull rather than filtered per sheet open: a turf holds a
  // few thousand people and the sheet opens on every pin tap.
  const peopleByHousehold = useMemo(() => {
    const map = new Map<string, PersonWithContact[]>();
    if (!source) return map;
    for (const person of withContacts(source.people, source.contacts)) {
      const list = map.get(person.household_id);
      if (list) list.push(person);
      else map.set(person.household_id, [person]);
    }
    // Oldest first: the registered voter most likely to answer a weekday door.
    for (const list of map.values()) list.sort((a, b) => (b.age ?? 0) - (a.age ?? 0));
    return map;
  }, [source]);

  const notesByHousehold = useMemo(() => {
    const map = new Map<string, LocalHouseNote[]>();
    for (const note of source?.notes ?? []) {
      const list = map.get(note.household_id);
      if (list) list.push(note);
      else map.set(note.household_id, [note]);
    }
    // Oldest first: a note thread reads as a history, not a stack.
    for (const list of map.values()) {
      list.sort((a, b) => a.created_at.localeCompare(b.created_at));
    }
    return map;
  }, [source]);

  const visitsByHousehold = useMemo(() => {
    const map = new Map<string, ReviewVisit[]>();
    for (const visit of source?.visits ?? []) {
      const list = map.get(visit.household_id);
      if (list) list.push(visit);
      else map.set(visit.household_id, [visit]);
    }
    // Newest first: a knock history is read from the last attempt backwards.
    for (const list of map.values()) {
      list.sort((a, b) => b.visited_at.localeCompare(a.visited_at));
    }
    return map;
  }, [source]);

  const value = useMemo<TurfData>(
    () => ({
      turf: reviewing ? remote?.turf ?? null : localTurf ?? null,
      rows,
      // `localTurf === undefined` covers the moment before either path has
      // been chosen, so the screen says "loading" rather than "empty turf".
      loading:
        localTurf === undefined ||
        (reviewing ? remote === null && remoteError === null : local === undefined),
      error: remoteError,
      reload: reviewing ? () => setAttempt((n) => n + 1) : () => void syncNow(),
      readOnly: reviewing,
      peopleAt: (householdId: string) => peopleByHousehold.get(householdId) ?? [],
      notesAt: (householdId: string) => notesByHousehold.get(householdId) ?? [],
      visitsAt: (householdId: string) => visitsByHousehold.get(householdId) ?? [],
    }),
    [
      reviewing,
      remote,
      remoteError,
      localTurf,
      local,
      rows,
      peopleByHousehold,
      notesByHousehold,
      visitsByHousehold,
    ],
  );

  return (
    <TurfDataContext.Provider value={value}>{children}</TurfDataContext.Provider>
  );
}

export function useTurfData(): TurfData {
  const ctx = useContext(TurfDataContext);
  if (!ctx) throw new Error('useTurfData must be used inside <TurfDataProvider>');
  return ctx;
}
